import { DefaultLocation } from "../constants/location";
import airportList from "../constants/airports.json";
import { PlaceType } from "../constants/PlaceType";
import { PointType } from "../constants/PointType";

const getServices = (() => {
  const services = {
    directionsService: undefined,
    autocompleteService: undefined,
    placesService: undefined,
    location: undefined,
    sessionToken: undefined,
  };

  return async () => {
    if (Object.values(services)
      .some(x => !x)) {
      //init
      services.directionsService = new window.google.maps.DirectionsService();
      services.autocompleteService = new window.google.maps.places.AutocompleteService();
      services.placesService = new window.google.maps.places.PlacesService(document.createElement("div"));
      services.location = new window.google.maps.LatLng(DefaultLocation);
      services.sessionToken = new window.google.maps.places.AutocompleteSessionToken();


    }
    return services;
  };
})();

export async function googleDirection(origin, destination, departureTime = undefined) {
  const { directionsService } = await getServices();

  return await new Promise((resolve => {
    directionsService.route(
      {
        origin,
        destination,
        drivingOptions: !!departureTime ? { departureTime } : undefined,
        travelMode: window.google.maps.DirectionsTravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      function (response, status) {
        if (status === window.google.maps.DirectionsStatus.OK) {
          resolve(response);
        }
      },
    );
  }));
}

const requiredTypesForAddressPlaceType = [
  "route",
  "street_address",
  "administrative_area_level_1",
  "administrative_area_level_2",
  "administrative_area_level_3",
  "administrative_area_level_4",
  "administrative_area_level_5",
  "country",
  "locality",
  "postal_code",
  "postal_code_prefix",
  "postal_code_suffix",
  "street_number",
  "sublocality",
  "sublocality_level_1",
  "sublocality_level_2",
  "sublocality_level_3",
  "sublocality_level_4",
  "sublocality_level_5",
  "premise",
  "subpremise",
];


export const getSuggestion = async (searchTerm) => {
  const { autocompleteService, placesService, location, sessionToken } = await getServices();
  const componentRestrictions = { country: "us" };
  if (searchTerm) {

    const placePredictions = await new Promise((resolve, reject) => {
      autocompleteService.getPlacePredictions(
        {
          input: searchTerm,
          componentRestrictions,
          location,
          sessionToken,
          radius: 50000,
        }
        , (values, status) => {
          if (status === "OK") {
            resolve(values);
          } else {
            console.log(values, status);
            reject(status);
          }
        });
    });

    const placeInfos = await Promise.all(placePredictions.map(placePrediction => {
      return new Promise(((resolve, reject) => {
        placesService.getDetails({
            placeId: placePrediction.place_id,
            componentRestrictions,
            sessionToken,
            fields: [ "address_component", "type", "name", "geometry" ],
          },
          (placeDetails, status) => {
            if (status === "OK") {
              resolve({
                placePrediction,
                placeDetails,
              });
            } else {
              console.log(placeDetails, status);
              reject(status);
            }
          });
      }));
    }));

    const suggestions = placeInfos.map(placeInfo => {
      const addressComponents = placeInfo.placeDetails.address_components;
      const location = {
        lat: placeInfo.placeDetails.geometry.location.lat(),
        lng: placeInfo.placeDetails.geometry.location.lng(),
      };
      const address = {
        streetNumber: addressComponents.find(x => x.types.includes("street_number"))?.long_name,
        street: addressComponents.find(x => x.types.includes("route"))?.long_name,
        city: (
          addressComponents.find(x => x.types.includes("sublocality_level_1")) ||
          addressComponents.find(x => x.types.includes("administrative_area_level_3")) ||
          addressComponents.find(x => x.types.includes("locality"))
        )?.long_name,
        state: addressComponents.find(x => x.types.includes("administrative_area_level_1"))?.short_name,
        zip: addressComponents.find(x => x.types.includes("postal_code"))?.long_name,
        county: addressComponents.find(x => x.types.includes("administrative_area_level_2"))?.long_name,
        country: addressComponents.find(x => x.types.includes("country"))?.short_name,
        location,
        full: undefined,
      };


      const place = {
        id: undefined,
        userLabel: undefined,
        type: PointType.SEARCH,
        placeType: PlaceType.LANDMARK,
        name: placeInfo.placeDetails.name,
        point: location,
        airport: undefined,
        address,
      };

      if (placeInfo.placeDetails.types.includes("airport")) {
        const airportInfo = airportList.find(item => item.zip === address.zip);
        place.placeType = PlaceType.AIRPORT;
        place.airport = {
          ...airportInfo || {},
          name: placeInfo.placeDetails.name,
        };
      } else if (requiredTypesForAddressPlaceType.some(x => placeInfo.placeDetails.types.includes(x))) {
        place.placeType = PlaceType.ADDRESS;
      }

      const getFullAddress = () => {
        let shortAddress = "";
        if (place.placeType === PlaceType.AIRPORT) {
          shortAddress = placeInfo.placePrediction?.structured_formatting?.main_text;
        } else {
          shortAddress = `${address.streetNumber || ""} ${address.street || ""}`.trim();
        }
        const secondLine = `${address.city || ""}, ${address.state || ""} ${address.zip || ""}, ${address.country || ""}`;
        return `${shortAddress ? shortAddress + ", " : ""}${secondLine}`;
      };

      address.full = getFullAddress();

      return place;
    });


    const isValidAddressWithoutStreetNumber = (x) => {
      return x.street && x.city && x.state &&
        x.zip && x.county && x.country &&
        Object.values(x.location)
          .every(y => !!y);
    };

    const isValid = (x) => {
      return x.streetNumber && isValidAddressWithoutStreetNumber(x);
    };

    const isValidAirportAddress = (x) => {
      return x.state && x.zip && x.country &&
        Object.values(x.location)
          .every(y => !!y);
    };

    return suggestions.filter(x => {
      switch (x.placeType) {
        case PlaceType.AIRPORT:
          return isValidAirportAddress(x.address);
        case PlaceType.ADDRESS:
          return isValidAddressWithoutStreetNumber(x.address);
        case PlaceType.LANDMARK:
          return isValid(x.address);
        default:
          return false;
      }
    });
  }

  return [];
};

